import React, { useEffect, useState } from 'react';
import { User, Mail, Lock, Building, MapPin, Save, Eye, EyeOff } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../components/ui/Toast';
import { supabaseClient } from '../lib/supabase';
import { getCompanies } from '../utils/companyManagement';
export function EmployeeProfilePage() {
  const {
    user
  } = useAuth();
  const {
    showToast
  } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [employeeData, setEmployeeData] = useState<any>(null);
  useEffect(() => {
    loadEmployeeData();
  }, [user]);
  const loadEmployeeData = async () => {
    if (!user?.employeeId) return;
    setLoading(true);
    try {
      const employees = await supabaseClient.getEmployees();
      const employee = employees.find((e: any) => e.id === user.employeeId);
      if (employee) {
        setEmployeeData(employee);
        setFormData({
          name: employee.name || '',
          email: employee.email || '',
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      }
    } catch (error) {
      console.error('Error loading employee data:', error);
      showToast('error', 'Error', 'No se pudo cargar la información');
    } finally {
      setLoading(false);
    }
  };
  const handleSave = async () => {
    // Validations
    if (!formData.name.trim()) {
      showToast('warning', 'Campo requerido', 'El nombre es obligatorio');
      return;
    }
    if (!formData.email.trim()) {
      showToast('warning', 'Campo requerido', 'El email es obligatorio');
      return;
    }
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      showToast('warning', 'Email inválido', 'Por favor ingresa un email válido');
      return;
    }
    // Password change validation
    if (formData.newPassword || formData.confirmPassword) {
      if (!formData.currentPassword) {
        showToast('warning', 'Contraseña actual requerida', 'Debes ingresar tu contraseña actual para cambiarla');
        return;
      }
      if (formData.currentPassword !== employeeData.password) {
        showToast('error', 'Contraseña incorrecta', 'La contraseña actual no es correcta');
        return;
      }
      if (formData.newPassword.length < 6) {
        showToast('warning', 'Contraseña muy corta', 'La nueva contraseña debe tener al menos 6 caracteres');
        return;
      }
      if (formData.newPassword !== formData.confirmPassword) {
        showToast('error', 'Contraseñas no coinciden', 'La nueva contraseña y la confirmación deben ser iguales');
        return;
      }
    }
    setSaving(true);
    try {
      const updateData: any = {
        name: formData.name.trim(),
        email: formData.email.trim()
      };
      // Only update password if new password is provided
      if (formData.newPassword) {
        updateData.password = formData.newPassword;
      }
      await supabaseClient.updateEmployee(user!.employeeId!, updateData);
      showToast('success', 'Perfil actualizado', 'Tu información ha sido actualizada correctamente');
      // Clear password fields
      setFormData({
        ...formData,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      // Reload data
      await loadEmployeeData();
    } catch (error) {
      console.error('Error updating profile:', error);
      showToast('error', 'Error', 'No se pudo actualizar el perfil');
    } finally {
      setSaving(false);
    }
  };
  const companies = getCompanies();
  const company = companies.find(c => c.id === employeeData?.company_id);
  const branch = company?.branches.find(b => b.id === employeeData?.branch_id);
  if (loading) {
    return <div className="p-4 lg:p-8 max-w-4xl mx-auto" data-id="element-1407">
        <div className="flex items-center justify-center py-20" data-id="element-1408">
          <div className="text-center" data-id="element-1409">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black dark:border-white mx-auto mb-4" data-id="element-1410"></div>
            <p className="text-gray-500 dark:text-gray-400" data-id="element-1411">
              Cargando perfil...
            </p>
          </div>
        </div>
      </div>;
  }
  return <div className="p-4 lg:p-8 max-w-4xl mx-auto" data-id="element-1412">
      {/* Header */}
      <div className="mb-8" data-id="element-1413">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-1414">
          Mi Perfil
        </h1>
        <p className="text-gray-500 dark:text-gray-400" data-id="element-1415">
          Administra tu información personal y contraseña
        </p>
      </div>

      <div className="space-y-6" data-id="element-1416">
        {/* Profile Info Card */}
        <Card data-id="element-1417">
          <div className="flex items-center gap-4 mb-6 pb-6 border-b border-gray-200 dark:border-gray-800" data-id="element-1418">
            <div className="h-20 w-20 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white font-bold text-3xl shrink-0" data-id="element-1419">
              {formData.name.charAt(0).toUpperCase()}
            </div>
            <div data-id="element-1420">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white" data-id="element-1421">
                {formData.name}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-1422">
                {employeeData?.position || 'Empleado'}
              </p>
            </div>
          </div>

          {/* Company Info (Read-only) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg" data-id="element-1423">
            <div data-id="element-1424">
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1" data-id="element-1425">
                Empresa
              </label>
              <div className="flex items-center gap-2 text-sm text-gray-900 dark:text-white" data-id="element-1426">
                <Building size={16} className="text-gray-400" data-id="element-1427" />
                {company?.name || 'N/A'}
              </div>
            </div>
            <div data-id="element-1428">
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1" data-id="element-1429">
                Sucursal
              </label>
              <div className="flex items-center gap-2 text-sm text-gray-900 dark:text-white" data-id="element-1430">
                <MapPin size={16} className="text-gray-400" data-id="element-1431" />
                {branch?.name || 'N/A'}
              </div>
            </div>
          </div>

          {/* Editable Fields */}
          <div className="space-y-4" data-id="element-1432">
            <div data-id="element-1433">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-1434">
                Nombre Completo
              </label>
              <div className="relative" data-id="element-1435">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-1436" />
                <input type="text" value={formData.name} onChange={e => setFormData({
                ...formData,
                name: e.target.value
              })} className="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" placeholder="Tu nombre completo" data-id="element-1437" />
              </div>
            </div>

            <div data-id="element-1438">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-1439">
                Email
              </label>
              <div className="relative" data-id="element-1440">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-1441" />
                <input type="email" value={formData.email} onChange={e => setFormData({
                ...formData,
                email: e.target.value
              })} className="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" placeholder="tu@email.com" data-id="element-1442" />
              </div>
            </div>
          </div>
        </Card>

        {/* Change Password Card */}
        <Card data-id="element-1443">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4" data-id="element-1444">
            Cambiar Contraseña
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6" data-id="element-1445">
            Deja estos campos vacíos si no deseas cambiar tu contraseña
          </p>

          <div className="space-y-4" data-id="element-1446">
            <div data-id="element-1447">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-1448">
                Contraseña Actual
              </label>
              <div className="relative" data-id="element-1449">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-1450" />
                <input type={showPassword ? 'text' : 'password'} value={formData.currentPassword} onChange={e => setFormData({
                ...formData,
                currentPassword: e.target.value
              })} className="w-full pl-10 pr-12 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" placeholder="Tu contraseña actual" data-id="element-1451" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" data-id="element-1452">
                  {showPassword ? <EyeOff size={18} data-id="element-1453" /> : <Eye size={18} data-id="element-1454" />}
                </button>
              </div>
            </div>

            <div data-id="element-1455">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-1456">
                Nueva Contraseña
              </label>
              <div className="relative" data-id="element-1457">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-1458" />
                <input type={showNewPassword ? 'text' : 'password'} value={formData.newPassword} onChange={e => setFormData({
                ...formData,
                newPassword: e.target.value
              })} className="w-full pl-10 pr-12 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" placeholder="Mínimo 6 caracteres" data-id="element-1459" />
                <button type="button" onClick={() => setShowNewPassword(!showNewPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" data-id="element-1460">
                  {showNewPassword ? <EyeOff size={18} data-id="element-1461" /> : <Eye size={18} data-id="element-1462" />}
                </button>
              </div>
            </div>

            <div data-id="element-1463">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2" data-id="element-1464">
                Confirmar Nueva Contraseña
              </label>
              <div className="relative" data-id="element-1465">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-1466" />
                <input type={showNewPassword ? 'text' : 'password'} value={formData.confirmPassword} onChange={e => setFormData({
                ...formData,
                confirmPassword: e.target.value
              })} className="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white" placeholder="Repite la nueva contraseña" data-id="element-1467" />
              </div>
            </div>
          </div>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end" data-id="element-1468">
          <Button onClick={handleSave} disabled={saving} leftIcon={saving ? undefined : <Save size={18} data-id="element-1470" />} className="px-8" data-id="element-1469">
            {saving ? 'Guardando...' : 'Guardar Cambios'}
          </Button>
        </div>
      </div>
    </div>;
}